import { SNAP_MINUTES } from '../shared/dayTimelineUtils';

/** dayOfWeek: 0=Sun … 6=Sat, 7=Public Holiday */
export const PUBLIC_HOLIDAY_DAY = 7;

export interface PricingColumn {
  dayOfWeek: number;
  label: string;
  shortLabel: string;
}

/** Mon–Sun column order plus public holiday. */
export const PRICING_COLUMNS: PricingColumn[] = [
  { dayOfWeek: 1, label: 'Monday', shortLabel: 'Mon' },
  { dayOfWeek: 2, label: 'Tuesday', shortLabel: 'Tue' },
  { dayOfWeek: 3, label: 'Wednesday', shortLabel: 'Wed' },
  { dayOfWeek: 4, label: 'Thursday', shortLabel: 'Thu' },
  { dayOfWeek: 5, label: 'Friday', shortLabel: 'Fri' },
  { dayOfWeek: 6, label: 'Saturday', shortLabel: 'Sat' },
  { dayOfWeek: 0, label: 'Sunday', shortLabel: 'Sun' },
  { dayOfWeek: PUBLIC_HOLIDAY_DAY, label: 'Public Holiday', shortLabel: 'PH' },
];

export interface PricingRuleRecord {
  id: string;
  buildingId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  hourlyRate: number;
  effectiveFrom?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PricingBlock {
  localId: string;
  serverId?: string;
  dayOfWeek: number;
  startMinutes: number;
  endMinutes: number;
  hourlyRate: number;
}

export const MINUTES_PER_DAY = 24 * 60;
export const PRICING_TIMELINE_HEIGHT_PX = 480;

export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export function minutesToTime(minutes: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, minutes));
  if (clamped >= MINUTES_PER_DAY) return '23:59';
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

export function endTimeToMinutes(endTime: string): number {
  if (endTime === '23:59') return MINUTES_PER_DAY;
  return timeToMinutes(endTime);
}

export function endMinutesToTime(endMinutes: number): string {
  if (endMinutes >= MINUTES_PER_DAY) return '23:59';
  return minutesToTime(endMinutes);
}

export function snapMinutes(minutes: number): number {
  const snapped = Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
  return Math.max(0, Math.min(MINUTES_PER_DAY, snapped));
}

export function minutesToY(minutes: number): number {
  return (minutes / MINUTES_PER_DAY) * PRICING_TIMELINE_HEIGHT_PX;
}

export function yToMinutes(y: number): number {
  const ratio = Math.max(0, Math.min(1, y / PRICING_TIMELINE_HEIGHT_PX));
  return snapMinutes(ratio * MINUTES_PER_DAY);
}

export function formatMinutesRange(startMinutes: number, endMinutes: number): string {
  return `${minutesToTime(startMinutes)} – ${endMinutesToTime(endMinutes)}`;
}

export function rateColor(rate: number, minRate: number, maxRate: number): string {
  if (maxRate <= minRate) return '#68d391';
  const t = (rate - minRate) / (maxRate - minRate);
  if (t >= 0.66) return '#f56565';
  if (t >= 0.33) return '#f6e05e';
  return '#68d391';
}

export function rulesToBlocks(rules: PricingRuleRecord[]): PricingBlock[] {
  return rules.map((rule) => ({
    localId: rule.id,
    serverId: rule.id,
    dayOfWeek: rule.dayOfWeek,
    startMinutes: timeToMinutes(rule.startTime),
    endMinutes: endTimeToMinutes(rule.endTime),
    hourlyRate: rule.hourlyRate,
  }));
}

export function blockToRulePayload(
  block: PricingBlock,
  buildingId: string,
  effectiveFrom?: string,
): Omit<PricingRuleRecord, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    buildingId,
    dayOfWeek: block.dayOfWeek,
    startTime: minutesToTime(block.startMinutes),
    endTime: endMinutesToTime(block.endMinutes),
    hourlyRate: block.hourlyRate,
    ...(effectiveFrom ? { effectiveFrom } : {}),
  };
}

export function blocksForDay(blocks: PricingBlock[], dayOfWeek: number): PricingBlock[] {
  return blocks
    .filter((b) => b.dayOfWeek === dayOfWeek)
    .sort((a, b) => a.startMinutes - b.startMinutes);
}

export interface CoverageGap {
  dayOfWeek: number;
  label: string;
  startMinutes: number;
  endMinutes: number;
}

export function findCoverageGaps(blocks: PricingBlock[]): CoverageGap[] {
  const gaps: CoverageGap[] = [];

  for (const column of PRICING_COLUMNS) {
    const dayBlocks = blocksForDay(blocks, column.dayOfWeek);
    let cursor = 0;

    for (const block of dayBlocks) {
      if (block.startMinutes > cursor) {
        gaps.push({
          dayOfWeek: column.dayOfWeek,
          label: column.label,
          startMinutes: cursor,
          endMinutes: block.startMinutes,
        });
      }
      cursor = Math.max(cursor, block.endMinutes);
    }

    if (cursor < MINUTES_PER_DAY) {
      gaps.push({
        dayOfWeek: column.dayOfWeek,
        label: column.label,
        startMinutes: cursor,
        endMinutes: MINUTES_PER_DAY,
      });
    }
  }

  return gaps;
}

export function hasFullCoverage(blocks: PricingBlock[]): boolean {
  return findCoverageGaps(blocks).length === 0;
}

export function blocksOverlap(a: PricingBlock, b: PricingBlock): boolean {
  if (a.dayOfWeek !== b.dayOfWeek || a.localId === b.localId) return false;
  return a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;
}

export function clampBlockToDay(block: PricingBlock): PricingBlock {
  let start = snapMinutes(block.startMinutes);
  let end = snapMinutes(block.endMinutes);
  if (end <= start) end = Math.min(start + SNAP_MINUTES, MINUTES_PER_DAY);
  return { ...block, startMinutes: start, endMinutes: end };
}

export function resolveDayOfWeekForDate(date: Date, publicHolidayDates: Set<string>): number {
  const key = date.toISOString().slice(0, 10);
  if (publicHolidayDates.has(key)) return PUBLIC_HOLIDAY_DAY;
  return date.getDay();
}

export function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const LEGACY_EFFECTIVE_FROM = '0000-01-01';

export function ruleEffectiveFrom(rule: PricingRuleRecord): string {
  return rule.effectiveFrom || LEGACY_EFFECTIVE_FROM;
}

export function getRulesEffectiveOnDate(rules: PricingRuleRecord[], date: Date): PricingRuleRecord[] {
  if (rules.length === 0) return [];

  const asOfKey = dateKey(date);
  const scheduleDates = [...new Set(rules.map(ruleEffectiveFrom))]
    .filter((d) => d <= asOfKey)
    .sort();

  const activeDate = scheduleDates[scheduleDates.length - 1] ?? LEGACY_EFFECTIVE_FROM;
  return rules.filter((rule) => ruleEffectiveFrom(rule) === activeDate);
}

export function getActiveRules(rules: PricingRuleRecord[], asOf: Date = new Date()): PricingRuleRecord[] {
  return getRulesEffectiveOnDate(rules, asOf);
}

export function newLocalId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
